import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHeader } from "../components/ui-bits";
import { formatCurrency } from "../lib/mmsPricing";
import {
  Sparkles, Image as ImageIcon, Loader2, ArrowLeft, Send, Upload,
  Check, HelpCircle, Pencil, RotateCcw, MessageSquare, Trash2, Settings2,
} from "lucide-react";

/**
 * MmsSimulatorPage — conversational garment-pricing simulator.
 *
 * Default UX: photo upload → AI vision → dynamic guided questions →
 * deterministic pricing engine → sanitized commercial estimate. Never
 * exposes hours, hourly rate, coefficients, AI reasoning to the client.
 *
 * A discreet secondary link ("Preferisci compilare manualmente?") lets
 * power users open the legacy 6-section form at /mms-simulator/manual.
 */
export default function MmsSimulatorPage() {
  const nav = useNavigate();
  const { sessionId: routeSessionId } = useParams();
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [err, setErr] = useState("");
  const [savedList, setSavedList] = useState([]);
  const [freeTextValue, setFreeTextValue] = useState("");
  // Guard: after we've explicitly created a session in this render cycle we
  // don't want the useEffect on routeSessionId to also kick off /start-analysis.
  const analyzedRef = useRef(new Set());

  const fetchList = async () => {
    try { const { data } = await api.get("/mms/sessions"); setSavedList(data); }
    catch { /* silent */ }
  };

  useEffect(() => { fetchList(); }, []);

  useEffect(() => {
    if (!routeSessionId) { setSession(null); return; }
    (async () => {
      try {
        const { data } = await api.get(`/mms/sessions/${routeSessionId}`);
        setSession(data);
        // Only auto-kick the analysis when landing on an unanalysed session
        // that we haven't already handled in this browser tab.
        if (data.status === "image_uploaded" && !analyzedRef.current.has(routeSessionId)) {
          analyzedRef.current.add(routeSessionId);
          runAnalysis(routeSessionId);
        }
      } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeSessionId]);

  const runAnalysis = async (sid) => {
    setAnalyzing(true); setErr("");
    try {
      const { data } = await api.post(`/mms/sessions/${sid}/start-analysis`, {}, { timeout: 0 });
      setSession(data);
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setErr(detail);
      // Refresh session state (backend rolls back status to image_uploaded on failure)
      try {
        const { data } = await api.get(`/mms/sessions/${sid}`);
        setSession(data);
      } catch { /* silent */ }
    }
    finally { setAnalyzing(false); }
  };

  const startNew = async (files, articleName) => {
    if (!files?.length) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", f);
      fd.append("article_name", articleName);
      const { data } = await api.post("/mms/sessions", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 0,
      });
      setSession(data);
      // Navigating triggers the useEffect above which will call runAnalysis
      // once (guarded by analyzedRef). Do NOT invoke runAnalysis here or it
      // races with the effect and fires the vision LLM twice.
      analyzedRef.current.add(data.id);
      nav(`/mms-simulator/${data.id}`, { replace: true });
      fetchList();
      // Kick analysis directly since useEffect will skip due to guard above.
      runAnalysis(data.id);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const retryAnalysis = () => {
    if (session?.id) {
      analyzedRef.current.add(session.id);
      runAnalysis(session.id);
    }
  };

  const submitAnswer = async (answer, dontKnow = false) => {
    if (!session?.current_question) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/mms/sessions/${session.id}/answer`, {
        question_id: session.current_question.id,
        answer, dont_know: dontKnow,
      });
      setSession(data);
      setFreeTextValue("");
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const goBack = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/mms/sessions/${session.id}/back`);
      setSession(data);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const doCalculate = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/mms/sessions/${session.id}/calculate`, {}, { timeout: 0 });
      setSession(data);
      fetchList();
      toast.success("Preventivo generato");
    } catch (e) {
      // Structured missing-fields error: refresh the session so the UI shows
      // the exact list + "Completa informazione" buttons.
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "missing_required_fields") {
        try {
          const { data: fresh } = await api.get(`/mms/sessions/${session.id}`);
          setSession(fresh);
        } catch (_) { /* ignore */ }
        toast.error("Manca ancora qualche informazione obbligatoria.");
      } else {
        setErr(formatApiErrorDetail(detail) || e.message);
      }
    }
    finally { setBusy(false); }
  };

  const doRequestConfirmation = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/mms/sessions/${session.id}/request-confirmation`);
      setSession(data);
      fetchList();
      toast.success("Richiesta inviata a MMS Studio");
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const doEditAssumption = async (path, value) => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/mms/sessions/${session.id}/edit-assumption`, { path, value });
      setSession(data);
      toast.success("Ipotesi aggiornata");
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const doChangeGarment = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/mms/sessions/${session.id}/change-garment`);
      setSession(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    finally { setBusy(false); }
  };

  const doReopenField = async (fieldId) => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/mms/sessions/${session.id}/reopen-field`, { field_id: fieldId });
      setSession(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    finally { setBusy(false); }
  };

  const doDelete = async (sid) => {
    if (!window.confirm("Eliminare la simulazione?")) return;
    try {
      await api.delete(`/mms/sessions/${sid}`);
      if (session?.id === sid) { setSession(null); nav("/mms-simulator", { replace: true }); }
      fetchList();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="max-w-7xl mx-auto p-6 md:p-10" data-testid="mms-simulator-page">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div className="flex-1 min-w-0">
          <PageHeader
            eyebrow="MMS Studio"
            title="MMS Price Simulator"
            subtitle="Carica una foto del capo: l'AI di MMS Studio la analizza e ti fa poche domande mirate per costruire il preventivo."
          />
        </div>
        {!session && (
          <Link to="/mms-simulator/manual"
                className="text-[10px] uppercase tracking-widest text-[#525252] hover:text-[#0A0A0A] inline-flex items-center gap-1 whitespace-nowrap mt-4"
                data-testid="mms-manual-mode-link">
            <Settings2 size={11} /> Preferisci compilare manualmente?
          </Link>
        )}
      </div>

      {err && <div className="text-red-600 text-xs mb-4" data-testid="mms-chat-error">{err}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
        <div className="min-w-0">
          {!session && (
            <UploadZone onSubmit={startNew} busy={busy} err={err} />
          )}

          {session && (
            <SessionView
              session={session}
              analyzing={analyzing}
              err={err}
              onRetryAnalysis={retryAnalysis}
              busy={busy}
              freeTextValue={freeTextValue}
              setFreeTextValue={setFreeTextValue}
              onAnswer={submitAnswer}
              onBack={goBack}
              onCalculate={doCalculate}
              onRequestConfirmation={doRequestConfirmation}
              onEditAssumption={doEditAssumption}
              onChangeGarment={doChangeGarment}
              onReopenField={doReopenField}
              onNew={() => { setSession(null); nav("/mms-simulator", { replace: true }); }}
            />
          )}
        </div>

        <aside className="min-w-0" data-testid="mms-chat-history">
          <div className="label-caps mb-3">Preventivi in corso</div>
          {savedList.length === 0 ? (
            <div className="text-[11px] text-[#737373] italic">Nessuna simulazione ancora.</div>
          ) : (
            <ul className="space-y-1">
              {savedList.map((s) => (
                <li key={s.id} className={`group border ${session?.id === s.id ? "border-[#0A0A0A]" : "border-[#E5E5E5]"} p-3`}>
                  <div className="flex items-start gap-2">
                    <button className="flex-1 min-w-0 text-left" onClick={() => nav(`/mms-simulator/${s.id}`)} data-testid={`mms-chat-open-${s.id}`}>
                      <div className="text-xs text-[#0A0A0A] truncate">{s.article_name || s.name || "Simulazione"}</div>
                      <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-0.5">
                        {statusLabel(s.status)}
                      </div>
                      {s.estimate?.total != null && (
                        <div className="text-[11px] tabular-nums text-[#0A0A0A] mt-1">
                          {formatCurrency(s.estimate.total, s.estimate.currency || "EUR")}
                        </div>
                      )}
                    </button>
                    <button onClick={() => doDelete(s.id)} className="opacity-0 group-hover:opacity-100 text-[#737373] hover:text-red-600 transition-opacity"
                            data-testid={`mms-chat-delete-${s.id}`}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </aside>
      </div>
    </div>
  );
}


function UploadZone({ onSubmit, busy, err }) {
  const fileRef = useRef();
  const [dragging, setDragging] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [articleName, setArticleName] = useState("");

  const handleFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    const accepted = [];
    const rejected = [];
    for (const f of incoming) {
      const fname = (f.name || "").toLowerCase();
      const t = (f.type || "").toLowerCase();
      const ok = /^image\/(jpeg|png|webp|heic|heif)$/.test(t)
        || fname.endsWith(".jpg") || fname.endsWith(".jpeg")
        || fname.endsWith(".png") || fname.endsWith(".webp")
        || fname.endsWith(".heic") || fname.endsWith(".heif");
      if (ok) accepted.push(f); else rejected.push(f.name);
    }
    if (rejected.length) toast.error(`Formato non supportato: ${rejected.join(", ")}. Usa JPG, PNG, WEBP o HEIC.`);
    if (accepted.length === 0) return;
    setPendingFiles((cur) => [...cur, ...accepted].slice(0, 5));
  };

  const removeFile = (idx) => setPendingFiles((cur) => cur.filter((_, i) => i !== idx));

  const canSubmit = articleName.trim().length > 0 && pendingFiles.length > 0 && !busy;

  const submit = () => {
    if (!canSubmit) return;
    onSubmit(pendingFiles, articleName.trim());
  };

  return (
    <div className="card-dark p-8 md:p-12" data-testid="mms-chat-upload">
      <div className="flex items-center gap-2 mb-2"><Sparkles size={16} strokeWidth={1.5} /><div className="label-caps">Nuova simulazione</div></div>
      <h2 className="h-editorial text-2xl mb-3">Carica una foto del tuo capo</h2>
      <p className="text-xs text-[#525252] leading-relaxed mb-6">
        Foto, render, disegno tecnico o capo di riferimento. L'AI riconosce automaticamente
        il capo e ti chiede solo il necessario per arrivare al preventivo.
      </p>

      {/* Article name — required */}
      <div className="mb-6">
        <label className="label-caps block mb-2">Nome articolo / Modello <span className="text-red-600">*</span></label>
        <input
          type="text"
          className="input-dark w-full"
          value={articleName}
          onChange={(e) => setArticleName(e.target.value)}
          placeholder="Es. Bomber 01 — Collezione FW27"
          maxLength={140}
          data-testid="mms-article-name-input"
        />
      </div>

      {/* Drop zone — plain <div>, no <label> wrapping to avoid double-click on the hidden input */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => fileRef.current?.click()}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") fileRef.current?.click(); }}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
        className={`border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${dragging ? "border-[#0A0A0A] bg-[#FAFAFA]" : "border-[#E5E5E5]"}`}
        data-testid="mms-chat-dropzone"
      >
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif"
          multiple
          className="hidden"
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
          data-testid="mms-chat-file-input"
        />
        <Upload size={32} strokeWidth={1.5} className="mx-auto mb-3 text-[#737373]" />
        <div className="text-sm text-[#0A0A0A] mb-1">Trascina qui le foto o clicca per selezionarle</div>
        <div className="text-[11px] text-[#737373]">JPG · PNG · WEBP · HEIC · fino a 5 immagini · max ~16MB ciascuna</div>
      </div>

      {/* Selected files preview */}
      {pendingFiles.length > 0 && (
        <div className="mt-6" data-testid="mms-chat-file-list">
          <div className="label-caps mb-3">Immagini pronte ({pendingFiles.length}/5)</div>
          <ul className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {pendingFiles.map((f, i) => (
              <li key={`${f.name}-${i}`} className="border border-[#E5E5E5] p-2 flex items-start gap-3" data-testid={`mms-chat-file-item-${i}`}>
                <FilePreview file={f} />
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-[#0A0A0A] truncate" title={f.name}>{f.name}</div>
                  <div className="text-[10px] text-[#737373]">{prettySize(f.size)}</div>
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    className="mt-2 text-[10px] uppercase tracking-widest text-[#525252] hover:text-red-600 inline-flex items-center gap-1"
                    data-testid={`mms-chat-file-remove-${i}`}
                  >
                    <Trash2 size={10} /> Rimuovi
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {err && (
        <div className="mt-6 border border-red-300 bg-red-50 p-3 text-xs text-red-700" data-testid="mms-chat-upload-error">
          {err}
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button
          onClick={submit}
          disabled={!canSubmit}
          className="btn-gold inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="mms-chat-start-btn"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {busy ? "Sto caricando…" : "Analizza il capo"}
        </button>
        {!canSubmit && !busy && (
          <span className="text-[11px] text-[#737373]">
            {articleName.trim().length === 0
              ? "Inserisci il nome dell'articolo per continuare"
              : "Carica almeno una foto per continuare"}
          </span>
        )}
      </div>
    </div>
  );
}

function FilePreview({ file }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    const t = (file.type || "").toLowerCase();
    const fname = (file.name || "").toLowerCase();
    const isHeic = t.includes("heic") || t.includes("heif") || fname.endsWith(".heic") || fname.endsWith(".heif");
    // Browsers cannot render HEIC in an <img> tag — show a placeholder instead.
    if (isHeic) { setSrc(null); return; }
    const url = URL.createObjectURL(file);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  if (!src) {
    return (
      <div className="w-16 h-16 border border-[#E5E5E5] bg-[#FAFAFA] flex items-center justify-center flex-shrink-0 text-[#737373]">
        <ImageIcon size={18} strokeWidth={1.5} />
      </div>
    );
  }
  return <img src={src} alt={file.name} className="w-16 h-16 object-cover border border-[#E5E5E5] flex-shrink-0" />;
}

function prettySize(bytes) {
  if (!bytes) return "0 KB";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}


function SessionView({ session, analyzing, err, onRetryAnalysis, busy, freeTextValue, setFreeTextValue,
                       onAnswer, onBack, onCalculate, onRequestConfirmation,
                       onEditAssumption, onChangeGarment, onReopenField, onNew }) {
  const showAnalyzing = analyzing || session.status === "analyzing";
  const analysisFailed = !showAnalyzing && !session.current_question && session.status === "image_uploaded";
  const readyToQuote = !!session.ready_to_quote;
  const missing = session.missing_required_fields || [];
  return (
    <div className="space-y-6" data-testid="mms-chat-session">
      <SessionHeader session={session} onNew={onNew} />
      <ImageStrip images={session.images} />
      <RecognizedSummary
        recognized={session.recognized_summary}
        confidenceLabel={session.confidence_label}
        status={session.status}
        serviceFlags={session.estimate?.service_flags || session.recognized_summary?.service_flags}
        autoSelectedGarment={session.auto_selected_garment}
        alternativeGarments={session.alternative_garments}
        onChangeGarment={onChangeGarment}
        busy={busy}
        views={session.views_detected}
        imageLayout={session.image_layout}
        essentialCovered={readyToQuote}
      />

      {showAnalyzing ? (
        <AnalyzingCard />
      ) : analysisFailed ? (
        <AnalysisFailedCard err={err} onRetry={onRetryAnalysis} busy={busy} />
      ) : session.current_question ? (
        <ConversationBlock
          session={session} busy={busy}
          freeTextValue={freeTextValue} setFreeTextValue={setFreeTextValue}
          onAnswer={onAnswer} onBack={onBack}
        />
      ) : !readyToQuote && missing.length > 0 ? (
        // Something is still missing but the picker has nothing to serve
        // (typically because the user answered "Non lo so" on a critical
        // field). Show EXACTLY what's blocking and let the client reopen it.
        <MissingFieldsCard missing={missing} onReopenField={onReopenField} busy={busy} />
      ) : session.status === "ready_to_calculate" && readyToQuote ? (
        <ReadyCard onCalculate={onCalculate} busy={busy} session={session} />
      ) : session.status === "estimate_generated" || session.status === "confirmation_requested" || session.status === "mms_verified" || session.status === "mms_modified" ? (
        <EstimateCard
          session={session}
          onRequestConfirmation={onRequestConfirmation}
          onEditAssumption={onEditAssumption}
          onBack={onBack}
          busy={busy}
        />
      ) : null}

      <QaHistoryPreview session={session} />
    </div>
  );
}


function AnalysisFailedCard({ err, onRetry, busy }) {
  return (
    <div className="border border-red-300 bg-red-50 p-6" data-testid="mms-chat-analysis-error">
      <div className="text-red-700 text-sm mb-1 font-medium">L'AI non è riuscita ad analizzare la foto</div>
      <div className="text-red-700 text-xs mb-4">
        {err || "Errore temporaneo. Riprova tra qualche istante, oppure carica un'immagine diversa."}
      </div>
      <button
        onClick={onRetry}
        disabled={busy}
        className="btn-gold inline-flex items-center gap-2 disabled:opacity-50"
        data-testid="mms-chat-retry-btn"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
        Riprova analisi
      </button>
    </div>
  );
}


function SessionHeader({ session, onNew }) {
  return (
    <div className="flex items-baseline justify-between gap-4 pb-4 border-b border-[#E5E5E5]">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-[#737373]">Articolo</div>
        <div className="text-lg font-serif text-[#0A0A0A] truncate" data-testid="mms-chat-article-name">
          {session.article_name || session.name}
        </div>
      </div>
      <button onClick={onNew} className="text-[10px] uppercase tracking-widest text-[#525252] hover:text-[#0A0A0A] inline-flex items-center gap-1"
              data-testid="mms-chat-new-btn">
        <RotateCcw size={11} /> Nuova simulazione
      </button>
    </div>
  );
}


function ImageStrip({ images }) {
  if (!images?.length) return null;
  return (
    <div className="flex flex-wrap gap-2" data-testid="mms-chat-image-strip">
      {images.map((img, i) => (
        <div key={img.id || i} className="w-24 h-24 border border-[#E5E5E5] overflow-hidden bg-[#FAFAFA]">
          {img.thumbnail_b64 ? (
            <img src={`data:${img.mime || "image/jpeg"};base64,${img.thumbnail_b64}`} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#B3B3B3]"><ImageIcon size={20} /></div>
          )}
        </div>
      ))}
    </div>
  );
}


function AnalyzingCard() {
  return (
    <div className="card-dark p-8 text-center bg-[#FAFAFA]" data-testid="mms-chat-analyzing">
      <Loader2 size={32} className="animate-spin mx-auto mb-3 text-[#0A0A0A]" strokeWidth={1.5} />
      <div className="label-caps mb-1">Analisi AI in corso</div>
      <div className="text-xs text-[#525252]">Sto studiando la foto e preparando le domande giuste.</div>
    </div>
  );
}


function statusPhaseLabel(s, essentialCovered) {
  if (s === "collecting_information") {
    // Distinguish "raccolta" from "informazioni da completare" (blocking unknowns).
    return essentialCovered ? "Pronto per il calcolo" : "Raccolta informazioni";
  }
  return {
    image_uploaded: "Analisi immagine",
    analyzing: "Analisi immagine",
    ready_to_calculate: "Pronto per il calcolo",
    estimate_generated: "Preventivo pronto",
    confirmation_requested: "Inviato a MMS",
    mms_verified: "Verificato",
    mms_modified: "Modificato da MMS",
    expired: "Scaduto",
  }[s] || null;
}

function RecognizedSummary({ recognized, confidenceLabel, status, serviceFlags, autoSelectedGarment, alternativeGarments, onChangeGarment, busy, views, imageLayout, essentialCovered }) {
  if (!recognized) return null;
  const productionRequested = !!(serviceFlags && serviceFlags.production_requested);
  const items = [
    { label: "Capo riconosciuto", value: recognized.subcategory !== "—" ? recognized.subcategory : recognized.category },
    { label: "Vestibilità", value: recognized.fit },
    { label: "Servizio", value: recognized.service },
    productionRequested ? { label: "Quantità", value: recognized.quantity } : null,
  ].filter((i) => i && i.value && i.value !== "—" && i.value !== null);

  const showAutoBanner = !!(autoSelectedGarment && autoSelectedGarment.name);
  const hasAlternatives = Array.isArray(alternativeGarments) && alternativeGarments.length > 0;
  const phaseLabel = statusPhaseLabel(status, essentialCovered);
  // Only surface the AI-quality label when the analysis is done. Never mix it
  // with a "collecting info" phase — that produced the "Stima completa"
  // incoherence in a previous iteration.
  const showAiQuality = confidenceLabel && (status === "ready_to_calculate" || status === "estimate_generated");
  const viewsList = views ? Object.entries(views) : [];

  if (items.length === 0 && !phaseLabel && !showAutoBanner) return null;
  return (
    <div className="border border-[#E5E5E5] bg-[#FAFAFA] p-4" data-testid="mms-chat-recognized">
      {showAutoBanner && (
        <div className="mb-3 pb-3 border-b border-[#E5E5E5] flex flex-wrap items-baseline gap-x-4 gap-y-2 justify-between" data-testid="mms-chat-auto-selected">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[#737373]">Ho identificato</div>
            <div className="text-sm text-[#0A0A0A] font-serif" data-testid="mms-chat-auto-selected-name">
              {autoSelectedGarment.name}. <span className="text-[#525252]">Procedo con l'analisi.</span>
            </div>
          </div>
          {hasAlternatives && (
            <button
              type="button"
              onClick={onChangeGarment}
              disabled={busy}
              className="text-[10px] uppercase tracking-widest text-[#525252] hover:text-[#0A0A0A] inline-flex items-center gap-1 disabled:opacity-50"
              data-testid="mms-chat-change-garment-btn"
            >
              <RotateCcw size={11} /> Cambia capo
            </button>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
        <div className="label-caps" data-testid="mms-chat-status-label">
          Ecco cosa ho capito {phaseLabel && <span className="text-[#737373] ml-1">· {phaseLabel}</span>}
        </div>
        {showAiQuality && (
          <div className="text-[10px] uppercase tracking-widest text-[#737373]" data-testid="mms-chat-confidence-label">
            Analisi immagine: <span className="text-[#0A0A0A]">{confidenceLabel}</span>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {items.map((it, i) => (
          <div key={i}>
            <div className="text-[10px] uppercase tracking-widest text-[#737373]">{it.label}</div>
            <div className="text-sm text-[#0A0A0A]">{String(it.value)}</div>
          </div>
        ))}
      </div>
      {viewsList.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[#E5E5E5]" data-testid="mms-chat-views-detected">
          <div className="text-[10px] uppercase tracking-widest text-[#737373] mb-1">
            Viste riconosciute dalla foto{imageLayout ? ` · ${imageLayout.replaceAll("_", " ")}` : ""}
          </div>
          <div className="flex flex-wrap gap-2">
            {viewsList.map(([k, v]) => {
              const label = { front: "Fronte", back: "Retro", side: "Laterale" }[k] || k;
              const state = v?.visible === true ? "visibile" : v?.visible === false ? "non visibile" : "non determinato";
              const color = v?.visible === true ? "text-[#0A0A0A]" : v?.visible === false ? "text-[#a12626]" : "text-[#737373]";
              return (
                <span key={k} className={`text-[11px] ${color}`} data-testid={`mms-chat-view-${k}`}>
                  {label}: {state}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


function ConversationBlock({ session, busy, freeTextValue, setFreeTextValue, onAnswer, onBack }) {
  const q = session.current_question;
  const progress = Math.min(100, ((session.question_count || 0) / (session.soft_cap || 6)) * 100);

  return (
    <div className="card-dark p-6 md:p-8" data-testid="mms-chat-question">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-full bg-[#E5E5E5] h-[2px]">
          <div className="bg-[#0A0A0A] h-full transition-all" style={{ width: `${progress}%` }} />
        </div>
        <div className="text-[10px] uppercase tracking-widest text-[#737373] whitespace-nowrap">
          {session.question_count}/{session.soft_cap}
        </div>
      </div>

      <div className="flex items-start gap-3 mb-6">
        <div className="w-8 h-8 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center flex-shrink-0">
          <Sparkles size={14} strokeWidth={1.5} />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-[#737373] mb-1">MMS AI</div>
          <div className="text-lg font-serif text-[#0A0A0A]" data-testid="mms-chat-question-text">{q.text}</div>
        </div>
      </div>

      {q.options?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
          {q.options.map((opt, i) => {
            const isPrev = q.previously_selected_value !== undefined
              && q.previously_selected_value !== null
              && String(q.previously_selected_value) === String(opt.value);
            return (
              <button
                key={i}
                disabled={busy}
                onClick={() => onAnswer(opt.value)}
                className={`text-left border p-3 transition-colors disabled:opacity-50 ${
                  isPrev
                    ? "border-[#0A0A0A] bg-[#FAFAFA] ring-1 ring-[#0A0A0A]"
                    : "border-[#E5E5E5] hover:border-[#0A0A0A] hover:bg-[#FAFAFA]"
                }`}
                data-testid={`mms-chat-opt-${q.id}-${i}`}
              >
                <div className="flex items-center gap-2">
                  {isPrev && <Check size={12} className="text-[#0A0A0A] flex-shrink-0" />}
                  <div className="text-sm text-[#0A0A0A]">{opt.label}</div>
                </div>
                {isPrev && (
                  <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-1">Risposta precedente</div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {q.allow_free_text && (
        <div className="flex items-center gap-2 mb-4">
          <input
            type={q.free_text_type === "number" ? "number" : "text"}
            className="input-dark flex-1"
            placeholder={q.free_text_type === "number" ? "Inserisci un numero…" : "Scrivi la tua risposta…"}
            value={freeTextValue}
            onChange={(e) => setFreeTextValue(e.target.value)}
            data-testid="mms-chat-free-text"
          />
          <button
            disabled={busy || !freeTextValue.trim()}
            onClick={() => onAnswer(q.free_text_type === "number" ? Number(freeTextValue) : freeTextValue.trim())}
            className="btn-gold inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="mms-chat-free-text-send"
          >
            <Send size={12} /> Invia
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-[#E5E5E5]">
        {q.allow_dont_know && (
          <button
            disabled={busy}
            onClick={() => onAnswer(null, true)}
            className="text-[11px] uppercase tracking-widest text-[#525252] hover:text-[#0A0A0A] inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="mms-chat-dontknow"
          >
            <HelpCircle size={11} /> Non lo so — richiedi supporto a MMS Studio
          </button>
        )}
        {(session.qa_history_count > 0 || session.question_count > 0) && (
          <button
            disabled={busy}
            onClick={onBack}
            className="text-[11px] uppercase tracking-widest text-[#525252] hover:text-[#0A0A0A] inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="mms-chat-back-btn"
          >
            <ArrowLeft size={11} /> Indietro
          </button>
        )}
      </div>
    </div>
  );
}


function ReadyCard({ onCalculate, busy, session }) {
  return (
    <div className="card-dark p-8 text-center" data-testid="mms-chat-ready">
      <div className="flex items-center justify-center gap-2 mb-3">
        <Check size={20} strokeWidth={1.5} />
        <div className="label-caps">Ho tutto quello che mi serve</div>
      </div>
      <div className="text-sm text-[#525252] mb-6 max-w-md mx-auto">
        Ho raccolto tutte le informazioni necessarie per il preventivo indicativo.
      </div>
      <button
        onClick={onCalculate}
        disabled={busy}
        className="btn-gold inline-flex items-center gap-2 disabled:opacity-50"
        data-testid="mms-chat-calculate-btn"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
        Calcola preventivo
      </button>
    </div>
  );
}


function MissingFieldsCard({ missing, onReopenField, busy }) {
  return (
    <div className="border border-amber-400 bg-amber-50 p-6" data-testid="mms-chat-missing-fields">
      <div className="flex items-center gap-2 mb-3">
        <HelpCircle size={16} className="text-amber-700" strokeWidth={1.5} />
        <div className="label-caps text-amber-900">Manca ancora qualche informazione</div>
      </div>
      <div className="text-sm text-[#525252] mb-4">
        Non posso ancora generare il preventivo. Devi ancora rispondere a:
      </div>
      <ul className="space-y-3" data-testid="mms-chat-missing-list">
        {missing.map((m) => (
          <li
            key={m.field_id}
            className="flex flex-wrap items-start justify-between gap-3 pb-3 border-b border-amber-200 last:border-b-0"
            data-testid={`mms-chat-missing-${m.field_id}`}
          >
            <div className="min-w-0 flex-1">
              <div className="text-sm text-[#0A0A0A] font-medium">{m.label}</div>
              {m.reason && <div className="text-xs text-[#525252] mt-0.5">{m.reason}</div>}
            </div>
            <button
              type="button"
              disabled={busy}
              onClick={() => onReopenField(m.field_id)}
              className="text-[11px] uppercase tracking-widest text-[#0A0A0A] hover:text-amber-800 inline-flex items-center gap-1 disabled:opacity-50 whitespace-nowrap"
              data-testid={`mms-chat-reopen-${m.field_id}`}
            >
              <RotateCcw size={11} /> Completa informazione
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}


function EstimateCard({ session, onRequestConfirmation, onEditAssumption, onBack, busy }) {
  const est = session.estimate;
  if (!est) return null;
  const currency = est.currency || "EUR";
  const flags = est.service_flags || {};
  const patternRequested = !!flags.pattern_requested;
  const sampleRequested = !!flags.sample_requested;
  const productionRequested = !!flags.production_requested;
  const qty = productionRequested ? (est.quantity || 0) : 0;
  const recognized = session.recognized_summary || {};
  // Separate line items — the client MUST see pattern and sample as
  // distinct rows (never merged as "Cartamodello + Primo campione — 382 €").
  const patternPrice = Number(est.pattern_price || 0);
  const samplePrice = Number(est.sample_price || 0);
  const productionTotal = Number(est.production_total || 0);
  const showPattern = est.show_pattern !== false && patternRequested && patternPrice > 0;
  const showSample = est.show_sample !== false && sampleRequested && samplePrice > 0;
  const showDevelopmentTotal = (est.show_development_total !== false) && showPattern && showSample;
  const showProduction = est.show_production !== false && productionRequested && productionTotal > 0;
  const productionLabel = (est.production_label || "Produzione").toUpperCase();
  const grandTotal = Number(est.grand_total ?? est.total ?? 0);

  // Sanity: the total must equal the sum of shown line items.
  const shownSum =
    (showPattern ? patternPrice : 0) +
    (showSample ? samplePrice : 0) +
    (showProduction ? productionTotal : 0);
  const totalMismatch = Math.abs(shownSum - grandTotal) > 0.5;

  return (
    <div className="space-y-4" data-testid="mms-chat-estimate">
      <div className="card-dark p-8 md:p-10">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles size={14} strokeWidth={1.5} />
          <div className="label-caps">AI ESTIMATE</div>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-[#737373] mb-6">Preventivo indicativo</div>

        {(recognized.subcategory && recognized.subcategory !== "—") && (
          <div className="text-sm text-[#525252] mb-4">
            {recognized.subcategory}
            {productionRequested && qty > 0 ? ` — ${qty} ${qty === 1 ? "pezzo" : "pezzi"}` : ""}
          </div>
        )}

        {showPattern && (
          <div className="py-3 border-b border-[#E5E5E5]">
            <div className="flex items-baseline justify-between">
              <div className="text-xs uppercase tracking-widest text-[#525252]">{(est.pattern_label || "Cartamodello").toUpperCase()}</div>
              <div className="font-serif text-2xl tabular-nums text-[#0A0A0A]" data-testid="mms-chat-pattern-price">
                {formatCurrency(patternPrice, currency)}
              </div>
            </div>
          </div>
        )}

        {showSample && (
          <div className="py-3 border-b border-[#E5E5E5]">
            <div className="flex items-baseline justify-between">
              <div className="text-xs uppercase tracking-widest text-[#525252]">{(est.sample_label || "Primo campione").toUpperCase()}</div>
              <div className="font-serif text-2xl tabular-nums text-[#0A0A0A]" data-testid="mms-chat-sample-price">
                {formatCurrency(samplePrice, currency)}
              </div>
            </div>
          </div>
        )}

        {showDevelopmentTotal && (
          <div className="py-3 border-b border-[#E5E5E5] bg-[#FAFAFA] -mx-2 px-2">
            <div className="flex items-baseline justify-between">
              <div className="text-xs uppercase tracking-widest text-[#0A0A0A]">{(est.development_label || "Totale sviluppo").toUpperCase()}</div>
              <div className="font-serif text-xl tabular-nums text-[#0A0A0A]" data-testid="mms-chat-development-total">
                {formatCurrency(Number(est.development_total || 0), currency)}
              </div>
            </div>
          </div>
        )}

        {showProduction && (
          <div className="py-3 border-b border-[#E5E5E5]">
            <div className="flex items-baseline justify-between">
              <div className="text-xs uppercase tracking-widest text-[#525252]">{productionLabel} {qty} {qty === 1 ? "pezzo" : "pezzi"}</div>
              <div className="text-sm tabular-nums text-[#0A0A0A]" data-testid="mms-chat-per-unit">
                {formatCurrency(est.production_unit_price ?? est.production_price_per_unit, currency)} / pz
              </div>
            </div>
            <div className="flex items-baseline justify-between mt-1">
              <div className="text-[10px] uppercase tracking-widest text-[#737373]">Totale produzione</div>
              <div className="text-sm tabular-nums text-[#0A0A0A]" data-testid="mms-chat-production-total">
                {formatCurrency(productionTotal, currency)}
              </div>
            </div>
          </div>
        )}

        <div className="pt-4 flex items-baseline justify-between">
          <div className="label-caps">Prezzo indicativo</div>
          <div className="font-serif text-3xl tabular-nums" data-testid="mms-chat-total">
            {formatCurrency(grandTotal, currency)}
          </div>
        </div>

        {totalMismatch && (
          <div className="mt-3 border border-red-300 bg-red-50 p-3 text-[11px] text-red-700" data-testid="mms-chat-total-mismatch">
            Attenzione: il totale non corrisponde alla somma delle voci. MMS Studio verificherà il preventivo prima di confermarlo.
          </div>
        )}

        {est.exclusions?.length > 0 && (
          <div className="mt-4 pt-4 border-t border-[#E5E5E5]">
            <div className="text-[10px] uppercase tracking-widest text-[#737373] mb-1">Esclusi dalla stima</div>
            <ul className="text-[11px] text-[#525252] space-y-0.5" data-testid="mms-chat-exclusions">
              {est.exclusions.map((x, i) => (<li key={i}>· {x}</li>))}
            </ul>
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-3">
          {session.status === "estimate_generated" && (
            <button
              onClick={onRequestConfirmation}
              disabled={busy}
              className="btn-gold inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="mms-chat-request-confirmation"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Richiedi conferma a MMS Studio
            </button>
          )}
          {session.status === "confirmation_requested" && (
            <div className="text-[11px] uppercase tracking-widest text-emerald-700 inline-flex items-center gap-1">
              <Check size={12} /> Richiesta inviata a MMS Studio
            </div>
          )}
          <button
            onClick={onBack}
            disabled={busy}
            className="text-[11px] uppercase tracking-widest text-[#525252] hover:text-[#0A0A0A] inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="mms-chat-edit-answers"
          >
            <Pencil size={11} /> Modifica risposte
          </button>
        </div>
      </div>

      {session.declarations && (
        <DeclarationsBlock declarations={session.declarations} />
      )}
    </div>
  );
}


function DeclarationsBlock({ declarations }) {
  const items = [];
  if (declarations.pattern_source?.value) {
    items.push({ label: "Origine cartamodello", value: declarations.pattern_source.label });
  }
  if (declarations.materials_provider?.value) {
    items.push({
      label: "Fornitura materiali",
      value: declarations.materials_provider.label +
        (declarations.materials_provider.quote_pending
          ? " · quotazione materiali separata"
          : ""),
    });
  }
  if (declarations.fabric_primary?.value) {
    items.push({ label: "Tessuto principale", value: declarations.fabric_primary.label });
  }
  if (declarations.fabric_secondary?.value) {
    items.push({
      label: "Tessuto secondario",
      value: `${declarations.fabric_secondary.label}${declarations.fabric_secondary.location ? " — " + declarations.fabric_secondary.location : ""}${declarations.fabric_secondary.scope ? " (" + declarations.fabric_secondary.scope + ")" : ""}`,
    });
  }
  if (declarations.personalizations?.label) {
    items.push({
      label: "Personalizzazioni richieste",
      value: `${declarations.personalizations.label} — costo da quotare separatamente da MMS Studio`,
    });
  }
  if (items.length === 0) return null;
  return (
    <div className="border border-[#E5E5E5] bg-[#FAFAFA] p-4" data-testid="mms-chat-declarations">
      <div className="label-caps mb-2">Riepilogo dichiarazioni</div>
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="text-xs text-[#525252] flex flex-wrap gap-x-2">
            <span className="text-[11px] text-[#0A0A0A] font-medium">{it.label}:</span>
            <span>{it.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}


function QaHistoryPreview({ session }) {
  const qc = session.question_count || 0;
  if (qc === 0) return null;
  return (
    <div className="text-[11px] text-[#737373]" data-testid="mms-chat-qa-history-preview">
      Domande completate: <span className="text-[#0A0A0A] tabular-nums">{qc}</span>
      {session.essential_covered ? <span className="ml-2">· Informazioni essenziali complete</span> : null}
    </div>
  );
}


function statusLabel(s) {
  return {
    image_uploaded: "Analisi immagine",
    analyzing: "Analisi immagine",
    collecting_information: "Raccolta informazioni",
    ready_to_calculate: "Pronto per il calcolo",
    estimate_generated: "Preventivo pronto",
    confirmation_requested: "Inviato a MMS",
    mms_verified: "Verificato",
    mms_modified: "Modificato da MMS",
    expired: "Scaduto",
  }[s] || s;
}
